/**
 * Running costs — the expanded V3.1 cost model.
 *
 * A train is a LOCOMOTIVE (by class / traction) plus a SET OF CARRIAGES, each
 * costed separately. Steam and diesel differ in running AND long-term overhaul
 * cost; infrastructure is costed per route-mile of the track actually operated
 * (Network Rail charges per PATH on its metals instead of our P-way); crew
 * carries employer on-costs; and an explicit overhaul & restoration provision
 * captures the boiler-ticket liability a cash-only view omits and a board asks
 * for.
 *
 * `costFromDeps` returns a `CostBreakdown` whose component subtotals sum exactly
 * to `total`; `costLines` renders those stored subtotals for the PDF appendix, so
 * the paper always reconciles to the headline contribution — never re-derive a
 * line from the rates. Fleet caps are enforced: a plan that exceeds the fleet is
 * reported inoperable, not quietly costed.
 *
 * Every editable number is a provenance-tagged `Rate` (measured / assumed /
 * claude), shown with its basis in the UI and printed in the assumptions register.
 */
import type {
  CostBreakdown, CostModel, Departure, FleetCaps, LocoClassRate, Network, Provenance,
  Rate, SetPlan, Traction,
} from './types'
import { designateDiagrams, locoOutOfPlace, type DesignateOptions } from './diagrams'
import { outOn } from './fleetAvailability'
import { engineLabel } from './fleetRegister'
import { applyRoster, type RosterDay } from './engineRoster'
import { journeyMiles } from './network'

const R = (value: number, prov: Provenance, basis: string, locked = false): Rate =>
  ({ value, prov, locked, basis })

/**
 * Shipped default cost model. Chris's 2026-07-22 figures are tagged `measured`
 * (fuel quantities/prices, footplate); the rest are sensible industry numbers
 * tagged `assumed` — the first thing to replace with the railway's real rate
 * cards. Illustrative loco classes stand in until workings are pinned to a
 * specific loco; the default class per traction is used for costing.
 */
export const DEFAULT_COST_MODEL: CostModel = {
  locos: [
    {
      code: '80072', klass: 'BR Std 4MT', traction: 'steam',
      fuelPerMile: R(8.4, 'measured', '28 kg/mi × £0.30/kg'),
      runMaintPerMile: R(6.5, 'assumed', 'running & light maintenance per mile'),
      // D2 audit 2026-08-19: was £16/mi, which implies a loco running only
      // ~4,000 mi/yr. The real ss26 Red pattern is ~264 steam-miles/day over
      // ~175 running days shared by 3 engines ≈ 15,000 mi/loco/yr, so a ~£650k
      // ticket over 10 years is ~£4.30/mi. £16/mi alone added ~£2.3k/day of
      // phantom cost to the as-drawn timetable.
      overhaulPerMile: R(4.5, 'assumed', '10-yr boiler ticket (~£650k) ÷ ~15k mi/yr real utilisation (ss26 WTT: ~264 steam-mi/day, ~175 days, 3 engines)'),
      footplatePerTurn: R(450, 'measured', 'driver + fireman, per diagram/day'),
      prepDisposalPerTurn: R(150, 'assumed', 'lighting-up + disposal per diagram/day'),
      fuelQtyPerMile: R(28, 'measured', 'coal kg per steam mile'),
      fuelUnitPrice: R(0.30, 'measured', '£300/tonne = £0.30/kg'),
      co2ePerUnit: 2.42,
    },
    {
      code: 'D6700', klass: 'Class 37', traction: 'diesel',
      fuelPerMile: R(6.6, 'measured', '6 l/mi × £1.10/l'),
      runMaintPerMile: R(3.5, 'assumed', 'running maintenance per mile'),
      overhaulPerMile: R(3, 'assumed', 'power-unit rebuild provision per mile'),
      footplatePerTurn: R(450, 'measured', 'driver + secondman, per diagram/day'),
      prepDisposalPerTurn: R(80, 'assumed', 'prep + stabling per diagram/day'),
      fuelQtyPerMile: R(6, 'measured', 'gas oil litres per diesel mile'),
      fuelUnitPrice: R(1.10, 'measured', '£/litre gas oil'),
      co2ePerUnit: 2.758,
    },
    {
      code: 'RCA', klass: 'Class 79 Autocar', traction: 'railcar',
      fuelPerMile: R(4.4, 'assumed', '4 l/mi × £1.10/l'),
      runMaintPerMile: R(2.4, 'assumed', 'running maintenance per mile'),
      overhaulPerMile: R(1.8, 'assumed', 'overhaul provision per mile'),
      footplatePerTurn: R(360, 'assumed', 'driver (+ guard where required), per diagram/day'),
      prepDisposalPerTurn: R(70, 'assumed', 'prep + stabling per diagram/day'),
      fuelQtyPerMile: R(4, 'assumed', 'gas oil litres per railcar mile'),
      fuelUnitPrice: R(1.10, 'measured', '£/litre gas oil'),
      co2ePerUnit: 2.758,
    },
  ],
  carriage: {
    runMaintPerVehDay: R(34, 'assumed', 'bearings, brakes, gangways — per vehicle per running day'),
    restorationPerVehDay: R(58, 'assumed', 'body/underframe restoration provision (~£90k / 20 yr)'),
    cleaningPerVehDay: R(18, 'assumed', 'cleaning & servicing per vehicle per running day'),
  },
  crew: {
    guardPerTurn: R(220, 'assumed', 'one guard per set in traffic, per turn'),
    ttiPerTurn: R(180, 'assumed', 'on-train ticket inspection / catering support per set'),
    onCostPct: R(0.18, 'assumed', 'NI + pension + holiday on all pay'),
  },
  infra: [
    { segmentKey: 'PIC-LEV', miles: 5.5, owner: 'nymr',
      pwayPerMile: R(145, 'assumed', 'permanent-way maintenance per route-mile per day'),
      sandtPerMile: R(70, 'assumed', 'signalling & telecoms per route-mile per day'),
      nrAccessPerPath: R(0, 'measured', 'NYMR metals — no NR access charge') },
    { segmentKey: 'LEV-GOA', miles: 7.0, owner: 'nymr',
      pwayPerMile: R(145, 'assumed', 'permanent-way maintenance per route-mile per day'),
      sandtPerMile: R(70, 'assumed', 'signalling & telecoms per route-mile per day'),
      nrAccessPerPath: R(0, 'measured', 'NYMR metals — no NR access charge') },
    { segmentKey: 'GOA-GRO', miles: 5.5, owner: 'nymr',
      pwayPerMile: R(145, 'assumed', 'permanent-way maintenance per route-mile per day'),
      sandtPerMile: R(70, 'assumed', 'signalling & telecoms per route-mile per day'),
      nrAccessPerPath: R(0, 'measured', 'NYMR metals — no NR access charge') },
    { segmentKey: 'GRO-WHB', miles: 6.0, owner: 'networkrail',
      pwayPerMile: R(0, 'measured', 'Network Rail track — access charge, not our P-way'),
      sandtPerMile: R(0, 'measured', 'Network Rail signalling'),
      nrAccessPerPath: R(260, 'assumed', 'NR access charge per path (Mode B)') },
  ],
  overhead: {
    signallingBoxPerDay: R(385, 'assumed', 'per signal box opening turn'),
    stationStaffPerDay: R(820, 'assumed', 'station staffing per operating day'),
    insurancePerDay: R(430, 'assumed', 'insurance per operating day'),
    marketingAdminPerDay: R(800, 'assumed', 'marketing & admin per operating day'),
  },
  crewSource: 'ratecard',
}

export const DEFAULT_FLEET: FleetCaps = {
  maxSteam: 3,
  maxDiesel: 2,
  maxRailcar: 1,
  maxEcsDiesel: 1,
}

/** Engines for empty-stock moves when a saved fleet predates the slot. */
export const ecsCapOf = (fleet: FleetCaps): number => fleet.maxEcsDiesel ?? 1

/** The default rate card used to cost a diagram of a given traction. */
function locoFor(model: CostModel, traction: Traction): LocoClassRate {
  return model.locos.find((l) => l.traction === traction) ?? model.locos[0]
}

/** traversals[i] = enabled departures that cross segment i. */
function segmentTraversals(net: Network, deps: Departure[]): number[] {
  const t = net.segments.map(() => 0)
  for (const d of deps) {
    if (!d.enabled || !Number.isFinite(d.minutes)) continue
    const lo = Math.min(d.o, d.d), hi = Math.max(d.o, d.d)
    net.segments.forEach((s, i) => { if (s.from >= lo && s.to <= hi) t[i] += 1 })
  }
  return t
}

/**
 * Infrastructure per operating day: P-way + S&T on the NYMR track ACTUALLY
 * operated (per route-mile, once/day — track maintenance is a fixed daily cost of
 * the miles you run over), plus the Network Rail access charge per path on its
 * segment. Segment rate cards align with network segments by index (both are
 * PIC-LEV, LEV-GOA, GOA-GRO, GRO-WHB). Mileage is read from the network, never
 * re-keyed here.
 */
function infraCost(net: Network, deps: Departure[], model: CostModel): { infra: number; infraNr: number; nrPaths: number } {
  const trav = segmentTraversals(net, deps)
  let infra = 0, infraNr = 0, nrPaths = 0
  net.segments.forEach((s, i) => {
    const rate = model.infra[i]
    if (!rate) return
    if (s.owner === 'networkrail') {
      nrPaths += trav[i]
      const c = trav[i] * rate.nrAccessPerPath.value
      infra += c; infraNr += c
    } else if (trav[i] > 0) {
      infra += s.miles * (rate.pwayPerMile.value + rate.sandtPerMile.value)
    }
  })
  return { infra, infraNr, nrPaths }
}

/** Signal boxes opened: NYMR crossing points within the operated span (NR excluded). */
function boxesOpenFor(net: Network, deps: Departure[]): number {
  const nr = net.segments.find((s) => s.owner === 'networkrail')
  const whitby = nr ? nr.to : net.stations.length - 1
  let lo = Infinity, hi = -Infinity
  for (const d of deps) {
    if (!d.enabled || !Number.isFinite(d.minutes)) continue
    lo = Math.min(lo, d.o, d.d); hi = Math.max(hi, d.o, d.d)
  }
  if (!Number.isFinite(lo)) return 0
  return net.crossingPoints.filter((cp) => cp < whitby && cp >= lo && cp <= hi).length
}

/**
 * Cost a departure list. Traction per diagram comes from designateDiagrams (so
 * loco pins and the Network Rail steam ban flow into the money). Pass the set
 * plan's real carriage count / sets-in-traffic when one exists.
 */
export function costFromDeps(
  net: Network,
  deps: Departure[],
  model: CostModel,
  dieselLoco: number,
  fleet: FleetCaps,
  carriagesOverride?: number,
  setsInTraffic?: number,
  /** A Firecon day: coal steam stood down (diagrams.ts::DesignateOptions); or the
   *  date's SEASON ROSTER (engineRoster.ts) — the engines the roster gave each turn. */
  opts?: DesignateOptions & { roster?: { colour: string; day: RosterDay } },
): CostBreakdown {
  // With a register in use every diagram is given a NAMED engine, Whitby
  // diagrams only to Whitby-enabled engines (Chris, 2026-09-11). With a season
  // roster for the date, the roster's engines stand (it already applied
  // availability, Firecon, the contract order and the planner's pins).
  const byId0 = new Map(deps.map((d) => [d.id, d]))
  const designated = opts?.roster && fleet.locos
    ? applyRoster(net, opts.roster.colour, designateDiagrams(net, deps, dieselLoco), byId0, opts.roster.day, fleet.locos)
    : designateDiagrams(net, deps, dieselLoco, fleet.locos, opts)
  const registerInUse = (fleet.locos ?? []).some((l) => l.inSimulation)
  const diagrams = designated.map((r) => r.diagram)
  const railcars = designated.filter((r) => r.traction === 'railcar').length
  const locoDiags = designated.filter((r) => r.traction !== 'railcar')
  const dieselN = designated.filter((r) => r.traction === 'diesel').length
  const steamN = designated.filter((r) => r.traction === 'steam').length
  // A diesel diagram made only of empty-stock moves is a STOCK-MOVE engine, not
  // a passenger one (Chris, 2026-09-04): it is counted against the ECS slot.
  const byId = new Map(deps.map((d) => [d.id, d]))
  const isEcsDiagram = (g: { legIds: string[] }) => g.legIds.length > 0 && g.legIds.every((id) => byId.get(id)?.positioning === true)
  const dieselEcs = designated.filter((r) => r.traction === 'diesel' && isEcsDiagram(r.diagram)).length
  const dieselService = dieselN - dieselEcs
  const carriages = carriagesOverride ?? locoDiags.reduce((t, r) => t + r.diagram.maxCoaches, 0)

  let steamRun = 0, dieselRun = 0, footplate = 0, prep = 0, overhaulLoco = 0
  let steamMiles = 0, dieselMiles = 0, coalTonnes = 0, co2eTonnes = 0, fuel = 0

  // Loco costing basis B (Chris, 2026-08-20): one all-in £ PER WORKING
  // (session) by traction — every timetabled service a diagram runs costs its
  // rate flat, so a 13-working day costs 13 × rate and dropping any single
  // working saves the full rate immediately ("when you reduce the number of
  // running turns in an efficiency play the costs will drop proportionally").
  // NOT per diagram: a day with the same 3 engines but half the trains MUST
  // cost less (Chris's June proof, 2026-08-20). Empty positioning moves are
  // charged too — they burn the same coal. The rate covers fuel, maintenance,
  // overhaul provision, footplate and prep (on-costs included), so those
  // components book £0 separately; coal, mileage and CO2e stay physical.
  const perWorking = model.locoCostMode === 'perTurn' ? model.locoPerTurn : undefined

  for (const { diagram, traction } of designated) {
    const lc = locoFor(model, traction)
    const miles = diagram.miles
    const workings = Math.max(1, diagram.legs.length)
    const legFuel = miles * lc.fuelPerMile.value
    const run = miles * lc.runMaintPerMile.value
    if (!perWorking) {
      overhaulLoco += miles * lc.overhaulPerMile.value
      footplate += lc.footplatePerTurn.value
      prep += lc.prepDisposalPerTurn.value
    }
    fuel += legFuel
    const qty = miles * lc.fuelQtyPerMile.value // kg (steam) or litres (diesel/railcar)
    if (traction === 'steam') {
      steamMiles += miles
      steamRun += perWorking ? workings * perWorking.steam.value : legFuel + run
      const tonnes = qty / 1000
      coalTonnes += tonnes
      co2eTonnes += tonnes * lc.co2ePerUnit               // t CO2e per tonne coal
    } else {
      dieselMiles += miles
      dieselRun += perWorking ? workings * perWorking.diesel.value : legFuel + run
      co2eTonnes += (qty * lc.co2ePerUnit) / 1000          // kg CO2e per litre → t
    }
  }

  // Carriages: real vehicle count in traffic.
  const c = model.carriage
  const carriageRun = carriages * (c.runMaintPerVehDay.value + c.cleaningPerVehDay.value)
  const restoration = carriages * c.restorationPerVehDay.value
  const overhaulProvision = overhaulLoco + restoration

  // Crew: a guard + on-train TTI per set in traffic; on-costs on ALL pay.
  const sets = setsInTraffic ?? (carriages > 0 ? Math.max(1, Math.round(carriages / 6)) : 0)
  const guards = sets * model.crew.guardPerTurn.value
  const tti = sets * model.crew.ttiPerTurn.value
  const basePay = footplate + prep + guards + tti
  const onCost = basePay * model.crew.onCostPct.value
  const crew = basePay + onCost

  const { infra, infraNr, nrPaths } = infraCost(net, deps, model)

  const boxesOpen = boxesOpenFor(net, deps)
  const o = model.overhead
  // A day with no services is a day the railway does not open: no boxes, no
  // station staffing, no marketing charge. An EMPTY timetable costing £2k/day
  // was how the empty timetable ended up with a running cost at all.
  const anyService = deps.some((d) => d.enabled && Number.isFinite(d.minutes))
  const overhead = !anyService ? 0 :
    boxesOpen * o.signallingBoxPerDay.value +
    o.stationStaffPerDay.value +
    o.insurancePerDay.value +
    o.marketingAdminPerDay.value

  // The fleet rule: passenger diesels within "Diesel locos available"; stock-move
  // diesels within "ECS stock moves" PLUS any passenger diesel standing idle —
  // an idle passenger engine may shunt stock, a stock-move engine never hauls
  // passengers (Chris, 2026-09-04).
  const overCap: string[] = []
  const idleDiesel = Math.max(0, fleet.maxDiesel - dieselService)
  const ecsCap = ecsCapOf(fleet) + idleDiesel
  if (steamN > fleet.maxSteam) overCap.push(`${steamN} steam diagrams needed but only ${fleet.maxSteam} available`)
  if (dieselService > fleet.maxDiesel) overCap.push(`${dieselService} diesel diagram${dieselService === 1 ? '' : 's'} on passenger trains but only ${fleet.maxDiesel} available`)
  if (dieselEcs > ecsCap) overCap.push(`${dieselEcs} empty-stock diesel diagram${dieselEcs === 1 ? '' : 's'} but only ${ecsCap} available (${ecsCapOf(fleet)} for stock moves${idleDiesel > 0 ? ` plus ${idleDiesel} idle passenger diesel` : ''})`)
  if (railcars > fleet.maxRailcar) overCap.push(`${railcars} railcar diagrams needed but only ${fleet.maxRailcar} available`)
  const fleetNeed = { steam: steamN, diesel: dieselService, ecs: Math.max(0, dieselEcs - idleDiesel), railcar: railcars }
  // Every engine starts and ends its day at a shed — Grosmont or Pickering. A
  // diagram that begins or ends anywhere else is an engine appearing from
  // nowhere or left behind overnight: REPORTED (the month is outside the
  // Brief), never quietly costed as though a spare engine were waiting there
  // (Chris, 2026-09-07: locomotives may not appear or vanish).
  const locoOut = locoOutOfPlace(net, designated, registerInUse, deps)
  // Each named engine's TIMETABLED miles this day — what its hire contract
  // charges (a chargeable mile is a timetabled mile; empty stock is not) — and
  // which engines worked at all (a day-rate contract's day).
  // A dated designation (fleetAvailability.ts): steam turns run diesel for want
  // of a steam engine that day, and the engines out that day with why.
  const steamSwapped = designated.filter((d) => d.swappedFromSteam).length
  const enginesOut = opts?.date && registerInUse ? outOn(fleet.locos, opts.date).map((o) => ({ label: engineLabel(o.engine), reason: o.reason })) : []
  const engineMiles: Record<string, number> = {}
  const engineWorked: string[] = []
  for (const { diagram, engine } of designated) {
    if (!engine) continue
    const key = String(engine.id)
    if (!engineWorked.includes(key)) engineWorked.push(key)
    let m = 0
    for (const id of diagram.legIds) { const d = byId.get(id); if (d && !d.positioning) m += journeyMiles(net, d.o, d.d) }
    engineMiles[key] = (engineMiles[key] ?? 0) + m
  }

  const total = steamRun + dieselRun + crew + carriageRun + overhaulProvision + infra + overhead

  // The MARGINAL / DAY-FIXED split — the economics that make per-working
  // decisions sane. P-way & S&T on our own metals, the signal boxes and the
  // fixed overheads are the cost of OPENING THE RAILWAY for the day: they are
  // not avoided by dropping one train, so they must never be charged against a
  // single working's add/keep decision (that is how Best fit concluded no
  // service could ever pay and built an empty timetable). Network Rail access
  // is genuinely per-path and stays marginal.
  const dayFixed = (infra - infraNr) + overhead
  const marginal = total - dayFixed

  return {
    diagrams, railcars, locos: locoDiags.length, dieselN, steamN, carriages, dieselService, dieselEcs, fleetNeed,
    steamMiles, dieselMiles, coalTonnes, co2eTonnes, fuel,
    steamRun, dieselRun, footplate, prep, guards, tti, onCost, crew,
    carriageRun, overhaulLoco, restoration, overhaulProvision,
    infra, infraNr, overhead, boxesOpen, nrPaths, total, dayFixed, marginal, overCap, locoOut, engineMiles, engineWorked, steamSwapped, enginesOut,
  }
}

/** Convenience: cost a departure list against a set plan. */
export function costWithPlan(
  net: Network,
  deps: Departure[],
  plan: SetPlan | null,
  model: CostModel,
  dieselLoco: number,
  fleet: FleetCaps
): CostBreakdown {
  return costFromDeps(net, deps, model, dieselLoco, fleet, plan?.carriages, plan?.setsInTraffic)
}

/** Line-by-line breakdown for the PDF's cost appendix. Reads stored subtotals so
 *  the lines always sum to `total`. */
export interface CostLine { label: string; detail: string; amount: number }

export function costLines(c: CostBreakdown, model: CostModel): CostLine[] {
  const steam = locoFor(model, 'steam')
  const diesel = locoFor(model, 'diesel')
  const car = model.carriage
  const carVeh = car.runMaintPerVehDay.value + car.cleaningPerVehDay.value
  const perWorking = model.locoCostMode === 'perTurn' ? model.locoPerTurn : undefined
  const steamW = perWorking && perWorking.steam.value > 0 ? Math.round(c.steamRun / perWorking.steam.value) : 0
  const dieselW = perWorking && perWorking.diesel.value > 0 ? Math.round(c.dieselRun / perWorking.diesel.value) : 0
  return [
    { label: perWorking ? 'Steam locomotives (per-working, all-in)' : 'Steam locomotives (running)',
      detail: perWorking
        ? `${steamW} working${steamW === 1 ? '' : 's'} × £${perWorking.steam.value}/working (fuel + maint + overhaul + footplate + prep)`
        : `${c.steamMiles.toFixed(0)} steam-mi × (£${steam.fuelPerMile.value}+£${steam.runMaintPerMile.value})/mi`,
      amount: c.steamRun },
    { label: perWorking ? 'Diesel & railcar (per-working, all-in)' : 'Diesel & railcar (running)',
      detail: perWorking
        ? `${dieselW} working${dieselW === 1 ? '' : 's'} × £${perWorking.diesel.value}/working (all-in)`
        : `${c.dieselMiles.toFixed(0)} mi × (£${diesel.fuelPerMile.value}+£${diesel.runMaintPerMile.value})/mi`,
      amount: c.dieselRun },
    { label: 'Footplate crew',
      detail: perWorking ? 'included in the per-working rate' : `${c.diagrams.length} diagram${c.diagrams.length === 1 ? '' : 's'} × footplate/turn`,
      amount: c.footplate },
    { label: 'Prep & disposal',
      detail: perWorking ? 'included in the per-working rate' : 'lighting-up / disposal per diagram',
      amount: c.prep },
    { label: 'Guards',
      detail: `per set in traffic × £${model.crew.guardPerTurn.value}/turn`,
      amount: c.guards },
    { label: 'On-train (TTI) / catering',
      detail: `per set in traffic × £${model.crew.ttiPerTurn.value}/turn`,
      amount: c.tti },
    { label: 'Employer on-costs',
      detail: `${Math.round(model.crew.onCostPct.value * 100)}% on all pay (NI + pension + holiday)`,
      amount: c.onCost },
    { label: 'Carriages (maint & cleaning)',
      detail: `${c.carriages} vehicle${c.carriages === 1 ? '' : 's'} × £${carVeh}/veh-day`,
      amount: c.carriageRun },
    { label: 'Overhaul & restoration provision',
      detail: perWorking
        ? 'carriage restoration (loco overhaul is in the per-working rate)'
        : 'loco boiler/power-unit + carriage restoration — the liability a cash view omits',
      amount: c.overhaulProvision },
    { label: 'Infrastructure (per mile + NR access)',
      detail: `P-way + S&T over used track${c.nrPaths > 0 ? ` + ${c.nrPaths} NR path${c.nrPaths === 1 ? '' : 's'}` : ''}`,
      amount: c.infra },
    { label: 'Fixed overhead',
      detail: `${c.boxesOpen} signal box${c.boxesOpen === 1 ? '' : 'es'} + stations + insurance + marketing/admin`,
      amount: c.overhead },
  ]
}

/** Every editable Rate in the model, labelled — for the assumptions register and
 *  the Cost-model stage. Ranked by sensitivity is the caller's job. */
export function costModelRates(model: CostModel): { label: string; rate: Rate }[] {
  const out: { label: string; rate: Rate }[] = []
  if (model.locoCostMode === 'perTurn' && model.locoPerTurn) {
    out.push({ label: 'Locomotive — steam, all-in £/working (basis B)', rate: model.locoPerTurn.steam })
    out.push({ label: 'Locomotive — diesel/railcar, all-in £/working (basis B)', rate: model.locoPerTurn.diesel })
  }
  for (const l of model.locos) {
    const p = `${l.klass} (${l.code})`
    out.push({ label: `${p} — fuel/mi`, rate: l.fuelPerMile })
    out.push({ label: `${p} — run maint/mi`, rate: l.runMaintPerMile })
    out.push({ label: `${p} — overhaul provision/mi`, rate: l.overhaulPerMile })
    out.push({ label: `${p} — footplate/turn`, rate: l.footplatePerTurn })
    out.push({ label: `${p} — prep & disposal/turn`, rate: l.prepDisposalPerTurn })
  }
  const c = model.carriage
  out.push({ label: 'Carriage — run maint/veh-day', rate: c.runMaintPerVehDay })
  out.push({ label: 'Carriage — restoration provision/veh-day', rate: c.restorationPerVehDay })
  out.push({ label: 'Carriage — cleaning/veh-day', rate: c.cleaningPerVehDay })
  out.push({ label: 'Crew — guard/turn', rate: model.crew.guardPerTurn })
  out.push({ label: 'Crew — TTI/turn', rate: model.crew.ttiPerTurn })
  out.push({ label: 'Crew — employer on-costs', rate: model.crew.onCostPct })
  for (const s of model.infra) {
    if (s.owner === 'networkrail') {
      out.push({ label: `Infra ${s.segmentKey} — NR access/path`, rate: s.nrAccessPerPath })
    } else {
      out.push({ label: `Infra ${s.segmentKey} — P-way/mi`, rate: s.pwayPerMile })
      out.push({ label: `Infra ${s.segmentKey} — S&T/mi`, rate: s.sandtPerMile })
    }
  }
  const o = model.overhead
  out.push({ label: 'Overhead — signalling/box/day', rate: o.signallingBoxPerDay })
  out.push({ label: 'Overhead — station staff/day', rate: o.stationStaffPerDay })
  out.push({ label: 'Overhead — insurance/day', rate: o.insurancePerDay })
  out.push({ label: 'Overhead — marketing/admin/day', rate: o.marketingAdminPerDay })
  return out
}
